import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CopyOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { distributionAdminApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;

interface UserBrief {
  id: number;
  telegram_id?: number;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  shop_created?: boolean;
}

interface BotBrief {
  id: number;
  bot_id?: number;
  bot_username?: string | null;
  bot_name?: string | null;
  default_distribution_rate?: number;
  is_active?: boolean;
}

interface ProductBrief {
  id: number;
  name: string;
  product_type: string;
  price: number;
  commission_rate: number;
  allow_referral: boolean;
  is_active: boolean;
}

interface DistributionOverview {
  total_members: number;
  active_members: number;
  distributable_products: number;
  distribution_orders: number;
  gross_commission: number;
  pending_payout_amount: number;
}

interface DistributionProductItem {
  product: ProductBrief;
  merchant?: UserBrief | null;
  bot?: BotBrief | null;
  rate_source: 'product' | 'bot_default';
  rate_source_label: string;
  current_rate: number;
  distribution_order_count: number;
  turnover_amount: number;
  gross_commission: number;
}

interface DistributionMemberItem {
  member_id: number;
  display_name: string;
  is_active: boolean;
  commission_rate: number;
  custom_commission_rate?: number | null;
  default_commission_rate: number;
  share_code: string;
  distribution_link: string;
  created_at?: string | null;
  distributor?: UserBrief | null;
  merchant?: UserBrief | null;
  bot?: BotBrief | null;
  order_count: number;
  turnover_amount: number;
  gross_commission: number;
  current_withdrawable_balance: number;
  total_withdrawn_amount: number;
}

interface DistributionOrderItem {
  order_id: number;
  order_number: string;
  amount: number;
  commission_amount: number;
  distribution_rate_snapshot: number;
  status: string;
  created_at?: string | null;
  paid_at?: string | null;
  completed_at?: string | null;
  member?: {
    member_id?: number | null;
    display_name?: string | null;
    distributor?: UserBrief | null;
  } | null;
  bot?: BotBrief | null;
  merchant?: UserBrief | null;
  product?: ProductBrief | null;
}

interface DistributionPayoutItem {
  id: number;
  amount: number;
  status: string;
  note?: string | null;
  processed_note?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  rejected_at?: string | null;
  distributor?: UserBrief | null;
}

interface ListResponse<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

type SortOrder = 'asc' | 'desc';
type ProductSortBy = 'created_at' | 'name' | 'current_rate';
type MemberSortBy = 'created_at' | 'commission_rate' | 'gross_commission' | 'order_count';
type OrderSortBy = 'created_at' | 'amount' | 'commission_amount';
type PayoutSortBy = 'created_at' | 'amount' | 'processed_at';

const formatUserName = (user?: UserBrief | null) => {
  if (!user) return '-';
  if (user.username) return `@${user.username}`;
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || `用户#${user.id}`;
};

const formatBotName = (bot?: BotBrief | null) => {
  if (!bot) return '-';
  return bot.bot_name || (bot.bot_username ? `@${bot.bot_username}` : `机器人#${bot.id}`);
};

const formatCurrency = (value?: number | null) => `${Number(value || 0).toFixed(2)} USDT`;

const DistributionManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('products');

  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(10);
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined);
  const [productSortBy, setProductSortBy] = useState<ProductSortBy>('created_at');
  const [productSortOrder, setProductSortOrder] = useState<SortOrder>('desc');

  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(10);
  const [memberSearchInput, setMemberSearchInput] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatus, setMemberStatus] = useState<string | undefined>(undefined);
  const [memberSortBy, setMemberSortBy] = useState<MemberSortBy>('created_at');
  const [memberSortOrder, setMemberSortOrder] = useState<SortOrder>('desc');

  const [orderPage, setOrderPage] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(10);
  const [orderSearchInput, setOrderSearchInput] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState<string | undefined>(undefined);
  const [orderSortBy, setOrderSortBy] = useState<OrderSortBy>('created_at');
  const [orderSortOrder, setOrderSortOrder] = useState<SortOrder>('desc');

  const [payoutPage, setPayoutPage] = useState(1);
  const [payoutPageSize, setPayoutPageSize] = useState(10);
  const [payoutSearchInput, setPayoutSearchInput] = useState('');
  const [payoutSearch, setPayoutSearch] = useState('');
  const [payoutStatus, setPayoutStatus] = useState<string | undefined>(undefined);
  const [payoutSortBy, setPayoutSortBy] = useState<PayoutSortBy>('created_at');
  const [payoutSortOrder, setPayoutSortOrder] = useState<SortOrder>('desc');

  const [editVisible, setEditVisible] = useState(false);
  const [editingMember, setEditingMember] = useState<DistributionMemberItem | null>(null);
  const [editForm] = Form.useForm<{ custom_commission_rate?: number | null; is_active: boolean }>();
  const payoutNoteRef = useRef('');

  const {
    data: overview,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useQuery({
    queryKey: ['distribution-overview'],
    queryFn: async () =>
      (await distributionAdminApi.getOverview()) as unknown as DistributionOverview,
  });

  const {
    data: productData,
    isLoading: productLoading,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: [
      'distribution-products',
      productPage,
      productPageSize,
      productSearch,
      productSortBy,
      productSortOrder,
    ],
    queryFn: async () =>
      (await distributionAdminApi.getProducts({
        page: productPage,
        page_size: productPageSize,
        search: productSearch || undefined,
        sort_by: productSortBy,
        sort_order: productSortOrder,
      })) as unknown as ListResponse<DistributionProductItem>,
    enabled: activeTab === 'products',
  });

  const selectedProduct = productData?.items?.find(item => item.product.id === selectedProductId);

  const {
    data: productMemberData,
    isLoading: productMemberLoading,
    refetch: refetchProductMembers,
  } = useQuery({
    queryKey: ['distribution-members-compact', selectedProduct?.bot?.id],
    queryFn: async () =>
      (await distributionAdminApi.getMembers({
        page: 1,
        page_size: 6,
        bot_id: selectedProduct?.bot?.id,
      })) as unknown as ListResponse<DistributionMemberItem>,
    enabled: activeTab === 'products' && !!selectedProduct?.bot?.id,
  });

  useEffect(() => {
    if (activeTab !== 'products') {
      return;
    }
    if (!productData?.items?.length) {
      setSelectedProductId(undefined);
      return;
    }
    const hasSelectedProduct = productData.items.some(
      item => item.product.id === selectedProductId
    );
    if (!hasSelectedProduct) {
      setSelectedProductId(productData.items[0].product.id);
    }
  }, [activeTab, productData, selectedProductId]);

  const {
    data: memberData,
    isLoading: memberLoading,
    refetch: refetchMembers,
  } = useQuery({
    queryKey: [
      'distribution-members',
      memberPage,
      memberPageSize,
      memberSearch,
      memberStatus,
      memberSortBy,
      memberSortOrder,
    ],
    queryFn: async () =>
      (await distributionAdminApi.getMembers({
        page: memberPage,
        page_size: memberPageSize,
        search: memberSearch || undefined,
        status: memberStatus,
        sort_by: memberSortBy,
        sort_order: memberSortOrder,
      })) as unknown as ListResponse<DistributionMemberItem>,
    enabled: activeTab === 'members',
  });

  const {
    data: orderData,
    isLoading: orderLoading,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: [
      'distribution-orders',
      orderPage,
      orderPageSize,
      orderSearch,
      orderStatus,
      orderSortBy,
      orderSortOrder,
    ],
    queryFn: async () =>
      (await distributionAdminApi.getOrders({
        page: orderPage,
        page_size: orderPageSize,
        search: orderSearch || undefined,
        status: orderStatus,
        sort_by: orderSortBy,
        sort_order: orderSortOrder,
      })) as unknown as ListResponse<DistributionOrderItem>,
    enabled: activeTab === 'orders',
  });

  const {
    data: payoutData,
    isLoading: payoutLoading,
    refetch: refetchPayouts,
  } = useQuery({
    queryKey: [
      'distribution-payouts',
      payoutPage,
      payoutPageSize,
      payoutSearch,
      payoutStatus,
      payoutSortBy,
      payoutSortOrder,
    ],
    queryFn: async () =>
      (await distributionAdminApi.getPayouts({
        page: payoutPage,
        page_size: payoutPageSize,
        search: payoutSearch || undefined,
        status: payoutStatus,
        sort_by: payoutSortBy,
        sort_order: payoutSortOrder,
      })) as unknown as ListResponse<DistributionPayoutItem>,
    enabled: activeTab === 'payouts',
  });

  const updateMemberMutation = useMutation({
    mutationFn: async (payload: {
      memberId: number;
      data: { custom_commission_rate?: number | null; is_active?: boolean };
    }) => distributionAdminApi.updateMember(payload.memberId, payload.data),
    onSuccess: () => {
      message.success('分销员信息已更新');
      setEditVisible(false);
      setEditingMember(null);
      queryClient.invalidateQueries(['distribution-members']);
      queryClient.invalidateQueries(['distribution-members-compact']);
      queryClient.invalidateQueries(['distribution-overview']);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || '更新分销员失败');
    },
  });

  const completePayoutMutation = useMutation({
    mutationFn: async (payload: { payoutId: number; processed_note?: string }) =>
      distributionAdminApi.completePayout(payload.payoutId, {
        processed_note: payload.processed_note,
      }),
    onSuccess: () => {
      message.success('提现申请已标记完成');
      queryClient.invalidateQueries(['distribution-payouts']);
      queryClient.invalidateQueries(['distribution-overview']);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || '处理提现失败');
    },
  });

  const rejectPayoutMutation = useMutation({
    mutationFn: async (payload: { payoutId: number; processed_note: string }) =>
      distributionAdminApi.rejectPayout(payload.payoutId, {
        processed_note: payload.processed_note,
      }),
    onSuccess: () => {
      message.success('提现申请已拒绝');
      queryClient.invalidateQueries(['distribution-payouts']);
      queryClient.invalidateQueries(['distribution-overview']);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || '拒绝提现失败');
    },
  });

  const openEditMember = (member: DistributionMemberItem) => {
    setEditingMember(member);
    editForm.setFieldsValue({
      custom_commission_rate: member.custom_commission_rate ?? null,
      is_active: member.is_active,
    });
    setEditVisible(true);
  };

  const handleSaveMember = async () => {
    const values = await editForm.validateFields();
    if (!editingMember) return;
    updateMemberMutation.mutate({
      memberId: editingMember.member_id,
      data: {
        is_active: values.is_active,
        custom_commission_rate:
          values.custom_commission_rate === undefined || values.custom_commission_rate === null
            ? null
            : Number(values.custom_commission_rate),
      },
    });
  };

  const handleCopy = async (value?: string | null) => {
    if (!value) {
      message.warning('暂无可复制内容');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      message.success('已复制');
    } catch {
      message.error('复制失败，请手动复制');
    }
  };

  const handleRefresh = () => {
    refetchOverview();
    if (activeTab === 'products') {
      refetchProducts();
      if (selectedProduct?.bot?.id) {
        refetchProductMembers();
      }
    }
    if (activeTab === 'members') refetchMembers();
    if (activeTab === 'orders') refetchOrders();
    if (activeTab === 'payouts') refetchPayouts();
  };

  const showCompleteConfirm = (record: DistributionPayoutItem) => {
    payoutNoteRef.current = '';
    Modal.confirm({
      title: '确认标记提现完成',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>申请人：{formatUserName(record.distributor)}</p>
          <p>
            金额：<strong>{formatCurrency(record.amount)}</strong>
          </p>
          <Input.TextArea
            rows={3}
            placeholder="可选：填写处理备注"
            style={{ marginTop: 12 }}
            onChange={e => {
              payoutNoteRef.current = e.target.value;
            }}
          />
        </div>
      ),
      okText: '确认完成',
      cancelText: '取消',
      onOk: () =>
        completePayoutMutation.mutateAsync({
          payoutId: record.id,
          processed_note: payoutNoteRef.current || undefined,
        }),
    });
  };

  const showRejectConfirm = (record: DistributionPayoutItem) => {
    payoutNoteRef.current = '';
    Modal.confirm({
      title: '确认拒绝提现申请',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>申请人：{formatUserName(record.distributor)}</p>
          <p>
            金额：<strong>{formatCurrency(record.amount)}</strong>
          </p>
          <Input.TextArea
            rows={3}
            placeholder="请输入拒绝原因"
            style={{ marginTop: 12 }}
            onChange={e => {
              payoutNoteRef.current = e.target.value;
            }}
          />
        </div>
      ),
      okText: '确认拒绝',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        if (!payoutNoteRef.current.trim()) {
          message.warning('请输入拒绝原因');
          throw new Error('reject_reason_required');
        }
        await rejectPayoutMutation.mutateAsync({
          payoutId: record.id,
          processed_note: payoutNoteRef.current.trim(),
        });
      },
    });
  };

  const productColumns: ColumnsType<DistributionProductItem> = [
    {
      title: '商品',
      key: 'product',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.product.name}</Text>
          <Text type="secondary">
            {record.product.product_type === 'group' ? '会员群' : '卡密'}
          </Text>
        </Space>
      ),
    },
    {
      title: '商户 / 机器人',
      key: 'owner',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatUserName(record.merchant)}</Text>
          <Text type="secondary">{formatBotName(record.bot)}</Text>
        </Space>
      ),
    },
    {
      title: '比例来源',
      dataIndex: 'rate_source_label',
      key: 'rate_source_label',
      width: 120,
      render: (value: string) => (
        <Tag color={value === '商品分销比例' ? 'blue' : 'gold'}>{value}</Tag>
      ),
    },
    {
      title: '当前比例',
      dataIndex: 'current_rate',
      key: 'current_rate',
      width: 110,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    {
      title: '分销订单',
      dataIndex: 'distribution_order_count',
      key: 'distribution_order_count',
      width: 110,
    },
    {
      title: '成交额',
      dataIndex: 'turnover_amount',
      key: 'turnover_amount',
      width: 120,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: '累计佣金',
      dataIndex: 'gross_commission',
      key: 'gross_commission',
      width: 120,
      render: (value: number) => (
        <Text strong className="text-green-600">
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 90,
      render: (_, record) => (
        <Tag color={record.product.is_active ? 'success' : 'default'}>
          {record.product.is_active ? '上架' : '下架'}
        </Tag>
      ),
    },
  ];

  const memberColumns: ColumnsType<DistributionMemberItem> = [
    {
      title: '分销员',
      key: 'member',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.display_name}</Text>
          <Text type="secondary">{formatUserName(record.distributor)}</Text>
        </Space>
      ),
    },
    {
      title: '关联机器人',
      key: 'bot',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatBotName(record.bot)}</Text>
          <Text type="secondary">{formatUserName(record.merchant)}</Text>
        </Space>
      ),
    },
    {
      title: '分享信息',
      key: 'share',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>分享码：{record.share_code}</Text>
          <Space size={4}>
            <Text type="secondary" ellipsis style={{ maxWidth: 220 }}>
              {record.distribution_link}
            </Text>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(record.distribution_link)}
            />
          </Space>
        </Space>
      ),
    },
    {
      title: '当前比例',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      width: 100,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    {
      title: '自定义比例',
      dataIndex: 'custom_commission_rate',
      key: 'custom_commission_rate',
      width: 110,
      render: (value?: number | null) =>
        value === null || value === undefined ? '-' : `${Number(value).toFixed(2)}%`,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'default'}>{value ? '启用' : '停用'}</Tag>
      ),
    },
    {
      title: '订单数',
      dataIndex: 'order_count',
      key: 'order_count',
      width: 90,
    },
    {
      title: '累计佣金',
      dataIndex: 'gross_commission',
      key: 'gross_commission',
      width: 120,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: '当前可提现余额',
      dataIndex: 'current_withdrawable_balance',
      key: 'current_withdrawable_balance',
      width: 140,
      render: (value: number) => (
        <Text strong className={Number(value || 0) < 0 ? 'text-red-600' : 'text-blue-600'}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: '累计提现',
      dataIndex: 'total_withdrawn_amount',
      key: 'total_withdrawn_amount',
      width: 120,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value?: string | null) => formatBeijingTime(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button size="small" onClick={() => openEditMember(record)}>
          编辑
        </Button>
      ),
    },
  ];

  const productMemberColumns: ColumnsType<DistributionMemberItem> = [
    {
      title: '分销商',
      key: 'member',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.display_name}</Text>
          <Text type="secondary">{formatUserName(record.distributor)}</Text>
        </Space>
      ),
    },
    {
      title: '关联机器人',
      key: 'bot',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatBotName(record.bot)}</Text>
          <Text type="secondary">{formatUserName(record.merchant)}</Text>
        </Space>
      ),
    },
    {
      title: '分享码',
      dataIndex: 'share_code',
      key: 'share_code',
      width: 130,
      render: (value: string) => value || '-',
    },
    {
      title: '当前比例',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      width: 110,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'default'}>{value ? '启用' : '停用'}</Tag>
      ),
    },
    {
      title: '累计佣金',
      dataIndex: 'gross_commission',
      key: 'gross_commission',
      width: 120,
      render: (value: number) => formatCurrency(value),
    },
  ];

  const orderColumns: ColumnsType<DistributionOrderItem> = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 150,
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: '分销员',
      key: 'member',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.member?.display_name || '-'}</Text>
          <Text type="secondary">{formatUserName(record.member?.distributor)}</Text>
        </Space>
      ),
    },
    {
      title: '商品 / 机器人',
      key: 'product',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.product?.name || '-'}</Text>
          <Text type="secondary">{formatBotName(record.bot)}</Text>
        </Space>
      ),
    },
    {
      title: '订单金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: '佣金金额',
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      width: 110,
      render: (value: number) => (
        <Text strong className="text-green-600">
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: '比例快照',
      dataIndex: 'distribution_rate_snapshot',
      key: 'distribution_rate_snapshot',
      width: 110,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          paid: 'processing',
          completed: 'success',
          refunded: 'error',
          pending: 'default',
          cancelled: 'default',
        };
        return <Tag color={colorMap[value] || 'default'}>{value || '-'}</Tag>;
      },
    },
    {
      title: '时间',
      key: 'time',
      width: 180,
      render: (_, record) =>
        formatBeijingTime(record.paid_at || record.completed_at || record.created_at),
    },
  ];

  const payoutColumns: ColumnsType<DistributionPayoutItem> = [
    {
      title: '申请人',
      key: 'distributor',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatUserName(record.distributor)}</Text>
          <Text type="secondary">ID: {record.distributor?.id || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          pending: 'gold',
          completed: 'success',
          rejected: 'error',
          cancelled: 'default',
        };
        return <Tag color={colorMap[value] || 'default'}>{value || '-'}</Tag>;
      },
    },
    {
      title: '申请备注',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (value?: string | null) => value || '-',
    },
    {
      title: '处理备注',
      dataIndex: 'processed_note',
      key: 'processed_note',
      ellipsis: true,
      render: (value?: string | null) => value || '-',
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value?: string | null) => formatBeijingTime(value),
    },
    {
      title: '处理时间',
      key: 'processed_at',
      width: 180,
      render: (_, record) => formatBeijingTime(record.completed_at || record.rejected_at),
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            disabled={record.status !== 'pending'}
            onClick={() => showCompleteConfirm(record)}
          >
            标记完成
          </Button>
          <Button
            size="small"
            danger
            disabled={record.status !== 'pending'}
            onClick={() => showRejectConfirm(record)}
          >
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'products',
      label: '分销商品',
      children: (
        <Space direction="vertical" size="middle" className="w-full">
          <Space>
            <Input.Search
              allowClear
              placeholder="搜索商品 / 商户 / 机器人"
              value={productSearchInput}
              onChange={e => setProductSearchInput(e.target.value)}
              onSearch={value => {
                setProductPage(1);
                setProductSearch(value.trim());
              }}
              style={{ width: 320 }}
            />
            <Select
              value={`${productSortBy}:${productSortOrder}`}
              style={{ width: 190 }}
              onChange={value => {
                const [sortBy, sortOrder] = value.split(':') as [ProductSortBy, SortOrder];
                setProductPage(1);
                setProductSortBy(sortBy);
                setProductSortOrder(sortOrder);
              }}
              options={[
                { value: 'created_at:desc', label: '创建时间：新 → 旧' },
                { value: 'created_at:asc', label: '创建时间：旧 → 新' },
                { value: 'name:asc', label: '商品名称：A → Z' },
                { value: 'current_rate:desc', label: '分销比例：高 → 低' },
                { value: 'current_rate:asc', label: '分销比例：低 → 高' },
              ]}
            />
          </Space>
          <Card
            size="small"
            title={`分销商列表${productMemberData?.total ? `（${productMemberData.total}）` : ''}`}
            extra={
              <Space size={8}>
                {selectedProduct ? (
                  <Text type="secondary">当前商品：{selectedProduct.product.name}</Text>
                ) : null}
                <Button type="link" size="small" onClick={() => setActiveTab('members')}>
                  查看全部
                </Button>
              </Space>
            }
          >
            <Table
              rowKey="member_id"
              columns={productMemberColumns}
              dataSource={productMemberData?.items || []}
              loading={productMemberLoading}
              size="small"
              scroll={{ x: 900 }}
              pagination={false}
            />
          </Card>
          <Table
            rowKey={record => record.product.id}
            columns={productColumns}
            dataSource={productData?.items || []}
            loading={productLoading}
            size="small"
            scroll={{ x: 1100 }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedProductId ? [selectedProductId] : [],
              onChange: selectedRowKeys => {
                setSelectedProductId(selectedRowKeys[0] as number | undefined);
              },
            }}
            onRow={record => ({
              onClick: () => setSelectedProductId(record.product.id),
            })}
            pagination={{
              current: productData?.page || productPage,
              pageSize: productData?.page_size || productPageSize,
              total: productData?.total || 0,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setProductPage(page);
                setProductPageSize(pageSize);
              },
            }}
          />
        </Space>
      ),
    },
    {
      key: 'members',
      label: '分销员',
      children: (
        <Space direction="vertical" size="middle" className="w-full">
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="搜索分销员 / 分享码 / 机器人"
              value={memberSearchInput}
              onChange={e => setMemberSearchInput(e.target.value)}
              onSearch={value => {
                setMemberPage(1);
                setMemberSearch(value.trim());
              }}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="状态"
              value={memberStatus}
              style={{ width: 140 }}
              onChange={value => {
                setMemberPage(1);
                setMemberStatus(value);
              }}
            >
              <Select.Option value="active">启用</Select.Option>
              <Select.Option value="inactive">停用</Select.Option>
            </Select>
            <Select
              value={`${memberSortBy}:${memberSortOrder}`}
              style={{ width: 190 }}
              onChange={value => {
                const [sortBy, sortOrder] = value.split(':') as [MemberSortBy, SortOrder];
                setMemberPage(1);
                setMemberSortBy(sortBy);
                setMemberSortOrder(sortOrder);
              }}
              options={[
                { value: 'created_at:desc', label: '创建时间：新 → 旧' },
                { value: 'created_at:asc', label: '创建时间：旧 → 新' },
                { value: 'gross_commission:desc', label: '累计佣金：高 → 低' },
                { value: 'order_count:desc', label: '订单数：高 → 低' },
                { value: 'commission_rate:desc', label: '比例：高 → 低' },
                { value: 'commission_rate:asc', label: '比例：低 → 高' },
              ]}
            />
          </Space>
          <Table
            rowKey="member_id"
            columns={memberColumns}
            dataSource={memberData?.items || []}
            loading={memberLoading}
            size="small"
            scroll={{ x: 1450 }}
            pagination={{
              current: memberData?.page || memberPage,
              pageSize: memberData?.page_size || memberPageSize,
              total: memberData?.total || 0,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setMemberPage(page);
                setMemberPageSize(pageSize);
              },
            }}
          />
        </Space>
      ),
    },
    {
      key: 'orders',
      label: '佣金订单',
      children: (
        <Space direction="vertical" size="middle" className="w-full">
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="搜索订单号 / 分销员 / 商品"
              value={orderSearchInput}
              onChange={e => setOrderSearchInput(e.target.value)}
              onSearch={value => {
                setOrderPage(1);
                setOrderSearch(value.trim());
              }}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="订单状态"
              value={orderStatus}
              style={{ width: 160 }}
              onChange={value => {
                setOrderPage(1);
                setOrderStatus(value);
              }}
            >
              <Select.Option value="paid">paid</Select.Option>
              <Select.Option value="completed">completed</Select.Option>
              <Select.Option value="refunded">refunded</Select.Option>
              <Select.Option value="pending">pending</Select.Option>
            </Select>
            <Select
              value={`${orderSortBy}:${orderSortOrder}`}
              style={{ width: 190 }}
              onChange={value => {
                const [sortBy, sortOrder] = value.split(':') as [OrderSortBy, SortOrder];
                setOrderPage(1);
                setOrderSortBy(sortBy);
                setOrderSortOrder(sortOrder);
              }}
              options={[
                { value: 'created_at:desc', label: '时间：新 → 旧' },
                { value: 'created_at:asc', label: '时间：旧 → 新' },
                { value: 'amount:desc', label: '订单金额：高 → 低' },
                { value: 'amount:asc', label: '订单金额：低 → 高' },
                { value: 'commission_amount:desc', label: '佣金：高 → 低' },
                { value: 'commission_amount:asc', label: '佣金：低 → 高' },
              ]}
            />
          </Space>
          <Table
            rowKey="order_id"
            columns={orderColumns}
            dataSource={orderData?.items || []}
            loading={orderLoading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={{
              current: orderData?.page || orderPage,
              pageSize: orderData?.page_size || orderPageSize,
              total: orderData?.total || 0,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setOrderPage(page);
                setOrderPageSize(pageSize);
              },
            }}
          />
        </Space>
      ),
    },
    {
      key: 'payouts',
      label: '提现申请',
      children: (
        <Space direction="vertical" size="middle" className="w-full">
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="搜索申请人 / 备注"
              value={payoutSearchInput}
              onChange={e => setPayoutSearchInput(e.target.value)}
              onSearch={value => {
                setPayoutPage(1);
                setPayoutSearch(value.trim());
              }}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="提现状态"
              value={payoutStatus}
              style={{ width: 160 }}
              onChange={value => {
                setPayoutPage(1);
                setPayoutStatus(value);
              }}
            >
              <Select.Option value="pending">pending</Select.Option>
              <Select.Option value="completed">completed</Select.Option>
              <Select.Option value="rejected">rejected</Select.Option>
              <Select.Option value="cancelled">cancelled</Select.Option>
            </Select>
            <Select
              value={`${payoutSortBy}:${payoutSortOrder}`}
              style={{ width: 190 }}
              onChange={value => {
                const [sortBy, sortOrder] = value.split(':') as [PayoutSortBy, SortOrder];
                setPayoutPage(1);
                setPayoutSortBy(sortBy);
                setPayoutSortOrder(sortOrder);
              }}
              options={[
                { value: 'created_at:desc', label: '申请时间：新 → 旧' },
                { value: 'created_at:asc', label: '申请时间：旧 → 新' },
                { value: 'amount:desc', label: '提现金额：高 → 低' },
                { value: 'amount:asc', label: '提现金额：低 → 高' },
                { value: 'processed_at:desc', label: '处理时间：新 → 旧' },
                { value: 'processed_at:asc', label: '处理时间：旧 → 新' },
              ]}
            />
          </Space>
          <Table
            rowKey="id"
            columns={payoutColumns}
            dataSource={payoutData?.items || []}
            loading={payoutLoading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={{
              current: payoutData?.page || payoutPage,
              pageSize: payoutData?.page_size || payoutPageSize,
              total: payoutData?.total || 0,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setPayoutPage(page);
                setPayoutPageSize(pageSize);
              },
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">
            分销中心
          </Title>
          <Text type="secondary">统一查看管理员视角下的分销商品、分销员、佣金订单与提现申请</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
          刷新
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="分销员总数"
              value={overview?.total_members || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="已启用分销员"
              value={overview?.active_members || 0}
              prefix={<ShareAltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="可分销商品数"
              value={overview?.distributable_products || 0}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="分销订单数"
              value={overview?.distribution_orders || 0}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="累计分销佣金"
              value={overview?.gross_commission || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="USDT"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card loading={overviewLoading}>
            <Statistic
              title="待处理提现金额"
              value={overview?.pending_payout_amount || 0}
              precision={2}
              suffix="USDT"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} items={tabItems} onChange={setActiveTab} />
      </Card>

      <Modal
        title="编辑分销员"
        open={editVisible}
        onOk={handleSaveMember}
        onCancel={() => {
          setEditVisible(false);
          setEditingMember(null);
        }}
        confirmLoading={updateMemberMutation.isLoading}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="分销员名称">
            <Input value={editingMember?.display_name} readOnly />
          </Form.Item>
          <Form.Item
            label="自定义比例 (%)"
            name="custom_commission_rate"
            extra={`留空则恢复默认比例 ${Number(editingMember?.default_commission_rate || 0).toFixed(2)}%`}
            rules={[
              {
                validator: (_, value) => {
                  if (value === null || value === undefined || value === '') {
                    return Promise.resolve();
                  }
                  if (Number(value) < 0 || Number(value) > 80) {
                    return Promise.reject(new Error('比例必须在 0-80 之间'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              max={80}
              precision={2}
              style={{ width: '100%' }}
              placeholder="留空恢复默认比例"
            />
          </Form.Item>
          <Form.Item label="是否启用" name="is_active" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DistributionManagement;
